import { ChangeDetectionStrategy, Component, Signal, WritableSignal, afterNextRender, computed, inject, signal } from '@angular/core';

import { BlockDirective, ElemDirective } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { ConsentService } from '@stair-craft/common/analytics';

const BEM_BLOCK: string = 'sc-cookie-banner';

@Component({
    selector: 'sc-cookie-banner',
    templateUrl: './cookie-banner.component.html',
    styleUrl: './cookie-banner.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,

        // rt-tools ui-kit
        RtButtonDirective,
    ],
    host: {
        class: BEM_BLOCK,
        '[class.sc-cookie-banner--visible]': 'visible()',
        '[attr.role]': '"region"',
        '[attr.aria-live]': '"polite"',
    },
})
export class CookieBannerComponent {
    readonly #consent: ConsentService = inject(ConsentService);

    /**
     * Stays `false` during SSR and through hydration — server-rendered HTML
     * must match the client-rendered HTML to avoid hydration mismatches.
     * Flipped to `true` in `afterNextRender`, i.e. after hydration has
     * finished and `ConsentService.hydrate()` has read localStorage. Only
     * then may the banner appear, and only if there is no stored decision —
     * eliminates the post-refresh flash.
     */
    readonly #hydrated: WritableSignal<boolean> = signal<boolean>(false);

    protected readonly visible: Signal<boolean> = computed(() => this.#hydrated() && this.#consent.decision() === null);

    constructor() {
        afterNextRender((): void => {
            this.#hydrated.set(true);
        });
    }

    protected accept(): void {
        this.#consent.accept();
    }

    protected decline(): void {
        this.#consent.decline();
    }
}
